/** 可行性等级。判定始终基于「有效推重比」（已扣除安全系数预留）。 */
export type VerdictLevel = 'infeasible' | 'risky' | 'feasible' | 'good' | 'ample' | 'unknown';

/** 结论徽标色调。取值是 Badge 色调集合的子集，这里单独声明以免领域层依赖 UI。 */
export type VerdictTone = 'critical' | 'warning' | 'caution' | 'ok' | 'accent' | 'neutral';

export interface Verdict {
  level: VerdictLevel;
  label: string;
  description: string;
  tone: VerdictTone;
}

export const VERDICT_TONES: Record<VerdictLevel, VerdictTone> = {
  infeasible: 'critical',
  risky: 'warning',
  feasible: 'caution',
  good: 'ok',
  ample: 'accent',
  unknown: 'neutral',
};

/**
 * 阈值表 —— 唯一事实来源，测试直接引用它做边界断言。
 * 判据是「有效推重比」：`总推力 × (1 − 预留) / 起飞质量`。
 */
export const VERDICT_THRESHOLDS = {
  infeasibleBelow: 1.0,
  riskyBelow: 1.2,
  feasibleBelow: 1.5,
  goodBelow: 2.2,
} as const;

export const VERDICT_INFO: Record<Exclude<VerdictLevel, 'unknown'>, Omit<Verdict, 'level' | 'tone'>> = {
  infeasible: {
    label: '不可行',
    description: '扣除安全系数预留后推重比不足 1，无法稳定悬停，需减重或提高动力。',
  },
  risky: {
    label: '高风险',
    description: '扣除预留后余量很低，姿态扰动或阵风下容易触底，建议提高动力或减重。',
  },
  feasible: {
    label: '可行（偏保守）',
    description: '满足安全系数要求，适合航拍、巡检等平缓飞行；机动余量不大。',
  },
  good: {
    label: '良好',
    description: '动力余量充足，可承受一定机动飞行或增加少量载荷。',
  },
  ample: {
    label: '充裕',
    description: '推重比很高，适合需要强机动或更大载重的场景。',
  },
};

const UNKNOWN_VERDICT: Verdict = {
  level: 'unknown',
  label: '数据不足',
  description: '缺少起飞质量或总推力，无法给出可行性判定。',
  tone: VERDICT_TONES.unknown,
};

/**
 * 按有效推重比给出结论。边界值归入更保守的一档：
 * 恰好 1.5 记为「可行」，恰好 2.2 记为「良好」。
 */
export function verdictFor(effectiveTwr: number | null): Verdict {
  if (effectiveTwr === null || !Number.isFinite(effectiveTwr) || effectiveTwr <= 0) {
    return UNKNOWN_VERDICT;
  }

  const level: Exclude<VerdictLevel, 'unknown'> =
    effectiveTwr < VERDICT_THRESHOLDS.infeasibleBelow
      ? 'infeasible'
      : effectiveTwr < VERDICT_THRESHOLDS.riskyBelow
        ? 'risky'
        : effectiveTwr < VERDICT_THRESHOLDS.feasibleBelow
          ? 'feasible'
          : effectiveTwr < VERDICT_THRESHOLDS.goodBelow
            ? 'good'
            : 'ample';

  return { level, tone: VERDICT_TONES[level], ...VERDICT_INFO[level] };
}
